import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Search, Upload, Users } from "lucide-react";
import { NAV_LINKS } from "../data";
import { Logo } from "./Logo";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  if (location.pathname.startsWith("/admin")) return null;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FAF7F2]/85 backdrop-blur-md border-b border-[#EAE3D2]"
          : "bg-transparent"
      }`}
    >
      {/* Top utility bar */}
      <div className="hidden lg:block border-b border-[#EAE3D2]/50 bg-[#0F1E3C] text-white">
        <div className="container-x flex items-center justify-between h-9 text-xs">
          <div className="flex items-center gap-5 text-white/70">
            <a href="mailto:HR@albanyllc.com" className="hover:text-white transition-colors">HR@albanyllc.com</a>
            <span>·</span>
            <span>650 Warrenville Rd, Suite 100, Lisle, IL 60532</span>
          </div>
          <div className="flex items-center gap-1">
            <Link to="/careers" data-testid="top-refer" className="px-3 py-1.5 rounded-full hover:bg-white/10 transition-colors flex items-center gap-1.5">
              <Users className="w-3 h-3" /> Refer a Friend
            </Link>
            <Link to="/careers" data-testid="top-search" className="px-3 py-1.5 rounded-full hover:bg-white/10 transition-colors flex items-center gap-1.5">
              <Search className="w-3 h-3" /> Search Jobs
            </Link>
            <Link to="/careers" data-testid="top-submit" className="px-3 py-1.5 rounded-full hover:bg-white/10 transition-colors flex items-center gap-1.5">
              <Upload className="w-3 h-3" /> Submit Resume
            </Link>
          </div>
        </div>
      </div>

      <div className="container-x flex items-center justify-between h-16 sm:h-20">
        <Logo size="md" data-testid="nav-logo" />

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.href}
              to={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 relative ${
                  isActive ? "text-[#E9610E]" : "text-[#0F1E3C] hover:text-[#E9610E]"
                }`
              }
              end={link.href === "/"}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-start"
            className="hidden sm:inline-flex btn-accent"
          >
            Request Talent <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="lg:hidden p-2 text-[#0F1E3C]"
            onClick={() => setOpen(!open)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-white border-t border-[#EAE3D2]"
            data-testid="mobile-menu"
          >
            <nav className="container-x py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className="text-base font-medium text-[#0F1E3C] py-3 border-b border-[#EAE3D2]"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta"
                className="btn-accent mt-4"
              >
                Request Talent <ArrowUpRight className="w-4 h-4" />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
